import type { ReactNode } from 'react';
import { Undo2, RefreshCw, Lightbulb, Layers } from 'lucide-react';
import type { GameResult, Move, PieceChar, Turn } from '@strategy4kids/protocol';
import { Button } from './Button.js';

export interface HudProps {
  readonly turn: Turn;
  readonly captured: { w: PieceChar[]; b: PieceChar[] };
  readonly moves: readonly Move[];
  /**
   * The overlay's name, already translated by the caller. `packages/ui` has no
   * i18n dependency, so it takes resolved strings — the pattern documented in
   * `InstallButton.tsx`. It used to build this itself from a hardcoded English
   * map while `heat-map-*` sat translated but unread in `locales/`, so a
   * French or Spanish kid saw English.
   */
  readonly heatmapLabel: string;
  readonly robotThinking: boolean;
  readonly result: GameResult | null;
  readonly disabled: boolean;
  readonly hideCoach?: boolean;
  /** A phone: status plus Undo / Restart stay in view; the heat map, coach, captured pieces and move list fold into a "More" row. */
  readonly compact?: boolean;
  /** The fold-away row's caption, already translated. */
  readonly moreLabel?: string;
  /** Compact mode only: extra panels (the coach, a hint) folded into the "More" row. */
  readonly children?: ReactNode;
  readonly onToggleHeatmap: () => void;
  readonly onCoach: () => void;
  readonly onUndo: () => void;
  readonly onRestart: () => void;
}

const PIECE_LABEL: Record<string, string> = {
  p: 'P',
  n: 'N',
  b: 'B',
  r: 'R',
  q: 'Q',
  k: 'K',
  m: 'M',
  x: 'X',
};

function moveText(moves: readonly Move[]): string {
  const out: string[] = [];
  for (let i = 0; i < moves.length; i += 2) {
    const white = moves[i]?.san ?? '';
    const black = moves[i + 1]?.san ?? '';
    out.push(`${Math.floor(i / 2) + 1}. ${white} ${black}`);
  }
  return out.join('\n');
}

export function Hud({
  turn,
  captured,
  moves,
  heatmapLabel,
  robotThinking,
  result,
  disabled,
  hideCoach = false,
  compact = false,
  moreLabel = 'More',
  children,
  onToggleHeatmap,
  onCoach,
  onUndo,
  onRestart,
}: HudProps) {
  const status =
    result && 'winner' in result
      ? result.winner === 'w'
        ? 'You win!'
        : 'Robot wins!'
      : result
        ? 'Game over — draw'
        : robotThinking
          ? "Robot's turn…"
          : turn === 'w'
            ? 'Your turn'
            : "Robot's turn";

  if (compact) {
    return (
      <div className="flex flex-col gap-2 rounded-3xl border-2 border-sun/30 bg-cream p-3 text-ink">
        <div className="flex items-center justify-between">
          <div className="text-2xl font-extrabold" data-testid="status" aria-live="polite">
            {status}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onUndo} disabled={disabled} className="flex-1 !px-3" data-testid="undo-button">
            <Undo2 className="mr-1 inline" size={16} />
            Undo
          </Button>
          <Button variant="ghost" onClick={onRestart} disabled={disabled} className="flex-1 !px-3" data-testid="restart-button">
            <RefreshCw className="mr-1 inline" size={16} />
            Restart
          </Button>
        </div>
        <details className="group">
          <summary className="flex min-h-[48px] cursor-pointer items-center justify-between rounded-xl px-2 font-bold text-ink">
            <span>{moreLabel}</span>
            <span aria-hidden="true" className="transition-transform group-open:rotate-180">▾</span>
          </summary>
          <div className="mt-2 flex flex-col gap-3">
            <div className="text-sm font-bold text-ink/80" data-testid="heatmap-label">
              {heatmapLabel}
            </div>
          <div className="flex flex-wrap gap-1" data-testid="captured">
        {captured.w.map((c, i) => {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: captured arrays hold duplicate piece chars; the index disambiguates siblings
            <span key={`w-${c}-${i}`} className="text-sm font-bold" data-testid="captured-w">
              {PIECE_LABEL[c]}
            </span>
          );
        })}
        <span className="mx-1" />
        {captured.b.map((c, i) => {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: captured arrays hold duplicate piece chars; the index disambiguates siblings
            <span key={`b-${c}-${i}`} className="text-sm font-bold text-ink/80" data-testid="captured-b">
              {PIECE_LABEL[c]}
            </span>
          );
        })}
          </div>

          <pre className="h-32 overflow-y-auto rounded-xl bg-white/60 p-2 text-sm font-semibold" data-testid="move-list">
        {moveText(moves)}
          </pre>

            <div className="flex flex-wrap gap-2">
              <Button onClick={onToggleHeatmap} disabled={disabled} className="!py-2 !px-3 text-sm" data-testid="heatmap-toggle">
                <Layers className="mr-1 inline" size={16} />
                {heatmapLabel}
              </Button>
              {!hideCoach && (
                <Button onClick={onCoach} disabled={disabled || robotThinking} className="!py-2 !px-3 text-sm" data-testid="coach-button">
                  <Lightbulb className="mr-1 inline" size={16} />
                  Ask Coach
                </Button>
              )}
            </div>
            {children}
          </div>
        </details>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-3xl border-2 border-sun/30 bg-cream p-4 text-ink">
      <div className="flex items-center justify-between">
        <div className="text-2xl font-extrabold" data-testid="status" aria-live="polite">
          {status}
        </div>
        <div className="text-sm font-bold text-ink/80" data-testid="heatmap-label">
          {heatmapLabel}
        </div>
      </div>

      <div className="flex flex-wrap gap-1" data-testid="captured">
        {captured.w.map((c, i) => {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: captured arrays hold duplicate piece chars; the index disambiguates siblings
            <span key={`w-${c}-${i}`} className="text-sm font-bold" data-testid="captured-w">
              {PIECE_LABEL[c]}
            </span>
          );
        })}
        <span className="mx-1" />
        {captured.b.map((c, i) => {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: captured arrays hold duplicate piece chars; the index disambiguates siblings
            <span key={`b-${c}-${i}`} className="text-sm font-bold text-ink/80" data-testid="captured-b">
              {PIECE_LABEL[c]}
            </span>
          );
        })}
      </div>

      <pre className="h-32 overflow-y-auto rounded-xl bg-white/60 p-2 text-sm font-semibold" data-testid="move-list">
        {moveText(moves)}
      </pre>

      <div className="flex flex-wrap gap-2">
        <Button onClick={onToggleHeatmap} disabled={disabled} className="!py-2 !px-3 text-sm" data-testid="heatmap-toggle">
          <Layers className="mr-1 inline" size={16} />
          {heatmapLabel}
        </Button>
        {!hideCoach && (
          <Button onClick={onCoach} disabled={disabled || robotThinking} className="!py-2 !px-3 text-sm" data-testid="coach-button">
            <Lightbulb className="mr-1 inline" size={16} />
            Ask Coach
          </Button>
        )}
        <Button variant="secondary" onClick={onUndo} disabled={disabled} className="!py-2 !px-3 text-sm" data-testid="undo-button">
          <Undo2 className="mr-1 inline" size={16} />
          Undo
        </Button>
        <Button variant="ghost" onClick={onRestart} disabled={disabled} className="!py-2 !px-3 text-sm" data-testid="restart-button">
          <RefreshCw className="mr-1 inline" size={16} />
          Restart
        </Button>
      </div>
    </div>
  );
}
